import { ExtensionManagerImpl as ExtensionManager } from '@ui-platform/extensibility'
import { ensureArray, hasOwn, isFunction, isNonemptyArray, isObjectLike } from '../utils'
import { setValuesInternal } from '../actions'
import { stateRef } from '../store'
import { __error, __info } from '../../init/debug'
import { SPARKY_FRAMEWORK } from '../constants'
import { getDatadogRum } from '../externals'
import libs from '../../libs/lazy-ui'
import type {
  BuiltInExtensionCandidate,
  ExportFnParams,
  ExtensionCandidate,
  ExtensionConfig,
  ExtensionPoint,
  ExtensionPoints,
  ExtensionPointsExports,
  Extensions,
  ExtensionsExports,
  ExtensionsTsgCache,
  InitedMicroApp,
  InitedMicroAppConfig,
  MicroAppExport,
} from '../microAppTypes'

const createExtensionManagerWithCache = () => {
  const extensionManager = new ExtensionManager()
  Object.defineProperty(extensionManager, 'cache', { value: new Map() as ExtensionsTsgCache })
  return extensionManager
}

export type ContributeInitExtensionsFn = (extensions?: ExtensionsExports) => void
export type ResolvePureExtensionCandiates = (candidates: Record<string, ExtensionCandidate>) => void
export const initBuiltInExtensions = (builtInExtensionCandidate: BuiltInExtensionCandidate, enableNavExtensionPoints: boolean) => {
  const extensionManager = createExtensionManagerWithCache()
  const builtInInitExtPointIdSet = new Set<string>(builtInExtensionCandidate.initExtensionPoints.map(point => point.id))
  const loadBuiltInExtensionPoints = enableNavExtensionPoints ? builtInExtensionCandidate.extensionPoints : builtInExtensionCandidate.initExtensionPoints
  if (isNonemptyArray(loadBuiltInExtensionPoints)) {
    for (const extensionPoint of loadBuiltInExtensionPoints) {
      try {
        extensionManager.registerExtensionPoint(extensionPoint)
      }
      catch (err) {
        __error(`Error registering framework extension point: ${extensionPoint.id}`, err)
      }
    }
  }
  if (isNonemptyArray(builtInExtensionCandidate.extensions)) {
    for (const extentionsConfig of builtInExtensionCandidate.extensions) {
      for (const ext of extentionsConfig.configs) {
        copyExtMicroAppMeta(ext, extentionsConfig.point, {
          id: SPARKY_FRAMEWORK,
          name: SPARKY_FRAMEWORK,
        })
      }
    }
  }
  const contributeExtension = extensionManager.contributeExtension.bind(extensionManager)
  const contributeInitExtensions: ContributeInitExtensionsFn = (extensions?: ExtensionsExports) => {
    if (isNonemptyArray(extensions)) {
      for (const { point, configs } of extensions) {
        if (builtInInitExtPointIdSet.has(point.id)) { // contribute init extensions asap
          for (const extension of ensureArray(configs)) {
            try {
              contributeExtension(point, extension)
            }
            catch (err) {
              __error(`Error contribute extension: ${extension.id} for init point ${point.id}`, err)
              getDatadogRum()?.addError(err, {
                type: 'client_error',
                extension_id: extension.id,
                extension_point_id: point.id,
                micro_app_id: extension.config.microAppId,
              })
            }
          }
        }
      }
    }
  }
  const resolvePureExtensionCandiates: ResolvePureExtensionCandiates = (candidates) => {
    const extensionPointConfigs = [] as ExtensionPoints
    const extensionsConfigs = [] as Extensions
    for (const candidate of Object.values(candidates)) {
      if (candidate?.isPure) {
        if (isNonemptyArray(candidate.extensionPoints)) {
          extensionPointConfigs.push(...candidate.extensionPoints)
        }
        if (isNonemptyArray(candidate.extensions)) {
          for (const extensions of candidate.extensions) {
            if (!builtInInitExtPointIdSet.has(extensions.point.id)) {
              extensionsConfigs.push(extensions)
            }
          }
        }
      }
    }
    register(extensionManager, extensionPointConfigs, extensionsConfigs)
  }
  contributeInitExtensions(builtInExtensionCandidate.extensions)
  return { extensionManager, contributeInitExtensions, resolvePureExtensionCandiates }
}

export type MicroAppMeta = { id: string, name: string }
const copyMicroAppMeta = <T extends object>(config: T, microAppMeta: MicroAppMeta) => {
  if (!hasOwn(config, 'microAppId')) {
    Object.defineProperties(config, {
      microAppId: {
        value: microAppMeta.id,
      },
      microAppName: {
        value: microAppMeta.name,
      },
    })
  }
}
const copyExtMicroAppMeta = <T = any>(extensionConfig: ExtensionConfig<T>, extensionPoint: ExtensionPoint<T>, microAppMeta: MicroAppMeta) => {
  if (!extensionConfig || !extensionConfig.id || !extensionPoint.id ||
    !(isObjectLike(extensionConfig.config) || isFunction(extensionConfig.config))) {
    return extensionConfig
  }
  if (!hasOwn(extensionConfig.config, 'extensionId')) {
    Object.defineProperties(extensionConfig.config, {
      extensionId: {
        value: extensionConfig.id,
      },
      extensionPointId: {
        value: extensionPoint.id,
      }
    })
  }
  copyMicroAppMeta(extensionConfig.config, microAppMeta)
  return extensionConfig
}
const copyExtPointMicroAppMeta = <T = any>(extensionPoint: ExtensionPoint<T>, microAppMeta: MicroAppMeta) => {
  if (extensionPoint?.id) {
    copyMicroAppMeta(extensionPoint, microAppMeta)
  }
  return extensionPoint
}

export const collectCandidates = async (
  microApps: MicroAppExport[],
  builtInExtensionCandidate: ExtensionCandidate,
  contributeInitExtensions: ContributeInitExtensionsFn,
  resolvePureExtensionCandiates: ResolvePureExtensionCandiates,
) => {
  const promises = microApps.map(async (microApp) => {
    const microAppConfig = (await microApp) as InitedMicroAppConfig | null | false | undefined
    if (microAppConfig) {
      const { appName, id = appName, extensions, extensionPoints, appVars } = microAppConfig
      const notPureExtensionCandiates = isFunction(microAppConfig) || isNonemptyArray(microAppConfig) ||
        Array.isArray(microAppConfig.navigation) || Boolean(microAppConfig.actions)
      const params = Object.freeze(Object.assign(Object.create(libs), {
        id,
        name: appName,
        vars: appVars,
      })) as ExportFnParams
      const exec = <T>(arrayOrFn: T): T => {
        if (isFunction(arrayOrFn)) {
          try {
            return arrayOrFn(params) as T
          }
          catch (err) {
            console.error('failed to execute extensions or extensionPoints function', err)
            getDatadogRum()?.addError(err, {
              type: 'client_error',
              micro_app_id: id,
            })
          }
        }
        return arrayOrFn
      }
      const execExts = exec as (extensions: ExtensionsExports | undefined) => Extensions | undefined
      const execExtPoints = exec as (extensions: ExtensionPointsExports | undefined) => ExtensionPoints | undefined
      if (extensions || extensionPoints) {
        const processedExtensions = execExts(extensions)?.filter(each => {
          if (!each.point) {
            console.error('Extension point not defined for extension config', each)
            return false
          }
          if (!Array.isArray(each.configs)) {
            console.error('Extension configs array is required', each)
            return false
          }
          return true
        }).map(each => {
          for (const config of each.configs) {
            copyExtMicroAppMeta(config, each.point, params)
          }
          return each
        })
        contributeInitExtensions(processedExtensions)
        return {
          id,
          appName,
          isPure: !notPureExtensionCandiates,
          extensions: processedExtensions,
          extensionPoints: execExtPoints(extensionPoints)?.map(point =>
            copyExtPointMicroAppMeta(point, params)
          ),
        } as ExtensionCandidate
      }
    }
    return undefined
  })
  const candidates = await Promise.all(promises.map(p => p.catch(() => undefined))) // ignore micro-app loading errors
  const extensionCandidates = {
    [SPARKY_FRAMEWORK]: builtInExtensionCandidate,
  } as Record<string, ExtensionCandidate>
  for (const candidate of candidates) {
    if (candidate) {
      extensionCandidates[candidate.id] = candidate
    }
  }
  resolvePureExtensionCandiates(extensionCandidates)
  __info('extension candidates', extensionCandidates)
  return extensionCandidates
}

export const initExtensionState = (microApps: MicroAppExport[], builtInExtensionCandidate: BuiltInExtensionCandidate, enableNavExtensionPoints: boolean) => {
  const {
    extensionManager,
    contributeInitExtensions,
    resolvePureExtensionCandiates,
  } = initBuiltInExtensions(builtInExtensionCandidate, enableNavExtensionPoints)
  const extensionCandidatesPromise = collectCandidates(
    microApps,
    builtInExtensionCandidate,
    contributeInitExtensions,
    resolvePureExtensionCandiates,
  )
  return { extensionCandidatesPromise, extensionManager }
}

export const register = (extensionManager: ExtensionManager, extensionPointsExports?: ExtensionPoints, extensionsExports?: Extensions) => {
  if (isNonemptyArray(extensionPointsExports)) {
    for (const extensionPoint of extensionPointsExports) {
      try {
        extensionManager.registerExtensionPoint(extensionPoint)
      }
      catch (err) {
        __error(`Error contribute extension point: ${extensionPoint.id}`, err)
        getDatadogRum()?.addError(err, {
          type: 'client_error',
          extension_point_id: extensionPoint.id,
          micro_app_id: (extensionPoint as any).microAppId,
        })
      }
    }
  }
  if (isNonemptyArray(extensionsExports)) {
    for (const { point, configs } of extensionsExports) {
      for (const extension of ensureArray(configs)) {
        try {
          extensionManager.contributeExtension(point, extension)
        }
        catch (err) {
          __error(`Error contribute extension: ${extension.id} for point ${point.id}`, err)
          getDatadogRum()?.addError(err, {
            type: 'client_error',
            extension_id: extension.id,
            extension_point_id: point.id,
            micro_app_id: extension.config.microAppId,
          })
        }
      }
    }
  }
  Object.defineProperties(extensionManager, {
    registerExtensionPoint: { value: () => Promise.reject(new Error('registerExtensionPoint method not allowed')) },
    contributeExtension: { value: () => Promise.reject(new Error('contributeExtension method not allowed')) }
  })
}

export const resolveExtensions = async (exportedMicroApps: InitedMicroApp[], candidates: Record<string, ExtensionCandidate>) => {
  if (!candidates || !exportedMicroApps) {
    return
  }
  const extensionManager = createExtensionManagerWithCache()
  const extensionPointConfigs = [] as ExtensionPoints
  const extensionsConfig = [] as Extensions
  const microAppIds = [SPARKY_FRAMEWORK].concat(exportedMicroApps.map(app => app.id).filter(Boolean) as string[])
  for (const microAppId of microAppIds) {
    const candidate = candidates[microAppId]
    if (candidate) {
      if (isNonemptyArray(candidate.extensionPoints)) {
        extensionPointConfigs.push(...candidate.extensionPoints)
      }
      if (isNonemptyArray(candidate.extensions)) {
        extensionsConfig.push(...candidate.extensions)
      }
    }
  }
  register(extensionManager, extensionPointConfigs, extensionsConfig)
  setValuesInternal({ extensionManager: extensionManager })
}

export const getExtensions = <T>(point: ExtensionPoint<T>): Array<T> => {
  const extensionManager = stateRef.current.extensionManager as ExtensionManager | undefined
  if (!extensionManager) {
    if (!stateRef.current.extensionCandidatesPromise) {
      console.error('Extension manager feature is not available yet.')
    }
    else {
      console.error('Extension manager is not initialized before micro-app is fully tsg loaded.')
    }
    return []
  }
  return extensionManager.getExtensions<T>(point)
}
